import React, {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import useUser from 'utils/useUser';
import { isMobile } from 'utils/deviceHelper';
import CloseIcon from 'assets/img/close.svg?react';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import { useWindow } from 'infrastructure/providers/window-manager-provider';
import useKey from 'infrastructure/hooks/utils/use-key';
import ChevronTop from 'components/atoms/icons/chevron-top';

import s from './styles.module.scss';

import type { FC } from 'react';
import type { MasterModalProps } from './types';

const BOUNDARY_LIMIT = 40;
let globalOpenedModalCount = 0;
const offsetStep = 20;

const MasterModal: FC<MasterModalProps> = ({
  isOpen,
  toggle,
  title,
  subTitle,
  children,
  size = 'medium',
  centered = false,
  modalClassName,
  bodyClassName,
  headerClassName,
  footerClassName,
  titleClassName,
  subTitleClassName,
  footer,
  subHeader,
  rightTitle,
  primaryButton,
  secondaryButton,
  onSave,
  saveLabel,
  isSaveLoading,
  isSaveDisabled,
  isMinimized,
  setIsMinimized,
  withStackOffset = false,
  zIndex,
  dataCy = 'masterModal',
  bodyRef,
  modalStyle,
  ...rest
}) => {
  const { key } = useKey();
  const id = `master-modal-${key}`;
  const { focus, zIndex: windowZIndex } = useWindow(id);
  const modalRef = useRef<HTMLDivElement>(null);
  const { deviceType } = useUser();
  const { t } = useTranslation();

  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: isMobile(deviceType) ? 10 : 170 + offsetStep * globalOpenedModalCount,
    y: 14 + offsetStep * globalOpenedModalCount,
  });
  const [defaultPosition, setDefaultPosition] = useState({
    x: isMobile(deviceType) ? 10 : 170 + offsetStep * globalOpenedModalCount,
    y: 14 + offsetStep * globalOpenedModalCount,
  });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });

  const getModalPosition = useCallback(() => {
    if (centered) return {};
    if (isMinimized) {
      return {
        top: window.innerHeight - 60,
        left: window.innerWidth - 400,
      };
    }
    return { top: position.y, left: position.x };
  }, [centered, isMinimized, position]);

  const style: React.CSSProperties = centered
    ? { ...modalStyle }
    : { ...getModalPosition(), zIndex: 1000 + windowZIndex, ...modalStyle };

  const handleMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    if (centered) return;
    const targetEl = e.target as HTMLElement;
    e.stopPropagation();
    if (targetEl?.dataset?.draggable !== 'true') return;
    setIsDragging(true);
    setInitialPosition({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || centered) return;
      const { innerWidth: vw, innerHeight: vh } = window;
      if (
        e.clientY <= BOUNDARY_LIMIT ||
        e.clientX <= BOUNDARY_LIMIT ||
        e.clientX >= vw - BOUNDARY_LIMIT ||
        e.clientY >= vh - BOUNDARY_LIMIT
      )
        return;
      setPosition({
        x: e.clientX - initialPosition.x,
        y: e.clientY - initialPosition.y,
      });
    },
    [isDragging, centered, initialPosition],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') toggle();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle, isOpen]);

  useEffect(() => {
    const currentCount = globalOpenedModalCount;
    globalOpenedModalCount++;
    return () => {
      globalOpenedModalCount = Math.max(0, currentCount);
    };
  }, []);

  useLayoutEffect(() => {
    if (centered || !modalRef.current) return;
    const newPos = {
      x: isMobile(deviceType)
        ? 10
        : window.innerWidth / 2 - modalRef.current.offsetWidth / 2,
      y: isMobile(deviceType)
        ? 14 + offsetStep * globalOpenedModalCount
        : window.innerHeight / 2 - modalRef.current.offsetHeight / 2,
    };
    setPosition(newPos);
    setDefaultPosition(newPos);
  }, [centered, deviceType]);

  useEffect(() => {
    if (centered || !modalRef.current) return;
    const el = modalRef.current;
    const observer = new ResizeObserver(() => {
      setPosition((prev) => {
        const maxY = window.innerHeight - el.offsetHeight - 10;
        if (prev.y > maxY) return { ...prev, y: Math.max(0, maxY) };
        return prev;
      });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [centered, modalRef.current]);

  const toggleMinimize = () => {
    if (isMinimized) {
      setPosition(defaultPosition);
      setIsMinimized?.(false);
    } else {
      setDefaultPosition(position);
      setIsMinimized?.(true);
    }
  };

  const close = () => toggle(!isOpen);

  const renderFooter = () => {
    if (footer === false) return null;
    if (footer !== undefined) {
      return (
        <div
          className={classNames(s.footer, footerClassName, {
            [s.hidden]: isMinimized,
          })}
          data-draggable={!centered || undefined}
          data-drag={isDragging || undefined}
        >
          {footer}
        </div>
      );
    }

    const hasPrimary = !!(primaryButton || onSave);
    const hasSecondary = !!(secondaryButton !== undefined || hasPrimary);
    if (!hasPrimary && !hasSecondary) return null;

    return (
      <div
        className={classNames(s.footer, footerClassName, {
          [s.hidden]: isMinimized,
        })}
        data-draggable={!centered || undefined}
        data-drag={isDragging || undefined}
      >
        {hasSecondary && (
          <BaseButton
            type="secondary"
            label={secondaryButton?.label ?? t('controls.cancel')}
            onClick={secondaryButton?.onClick ?? close}
          />
        )}
        {hasPrimary && (
          <BaseButton
            type="primary"
            label={primaryButton?.label ?? saveLabel ?? t('controls.save')}
            onClick={primaryButton?.onClick ?? onSave}
            loading={primaryButton?.loading ?? isSaveLoading}
            disabled={primaryButton?.disabled ?? isSaveDisabled}
          />
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      {...rest}
      ref={withStackOffset ? undefined : modalRef}
      id={id}
      style={style}
      className={classNames(
        s.modal,
        modalClassName,
        s[`size-${size}`],
        centered ? s['centered-mode'] : s['draggable-mode'],
        { [s.minimized]: isMinimized },
      )}
      onMouseDown={handleMouseDown}
      data-cy={dataCy}
      onFocus={focus}
      tabIndex={-1}
    >
      <div
        data-draggable={!centered || undefined}
        data-drag={isDragging || undefined}
        className={classNames(s.header, headerClassName)}
      >
        <div
          className={s['title-wrapper']}
          data-draggable={!centered || undefined}
        >
          <div
            className={classNames(s.title, titleClassName)}
            role="heading"
            aria-level={4}
            data-draggable={!centered || undefined}
          >
            {title}
          </div>
          {subTitle && (
            <div
              className={classNames(s.subtitle, subTitleClassName)}
              data-draggable={!centered || undefined}
            >
              {subTitle}
            </div>
          )}
        </div>

        {rightTitle && (
          <div
            className={s['right-title']}
            data-draggable={!centered || undefined}
          >
            {rightTitle}
          </div>
        )}

        <div className={s['header-actions']}>
          {typeof setIsMinimized === 'function' && (
            <button
              type="button"
              className={s['action-btn']}
              onClick={toggleMinimize}
              title={isMinimized ? 'Restore' : 'Minimize'}
            >
              <ChevronTop
                style={
                  isMinimized ? undefined : { transform: 'rotate(180deg)' }
                }
                width={16}
                height={16}
              />
            </button>
          )}

          <button
            type="button"
            className={s['action-btn']}
            onClick={close}
            title={t('controls.close')}
          >
            <CloseIcon width={12} height={12} />
          </button>
        </div>
      </div>

      {subHeader && <div className={s['sub-header']}>{subHeader}</div>}

      <div
        ref={bodyRef}
        className={classNames(s.body, bodyClassName, {
          [s.hidden]: isMinimized,
        })}
      >
        {children}
      </div>

      {renderFooter()}
    </div>
  );

  if (centered) {
    return ReactDOM.createPortal(
      <div
        className={s.overlay}
        style={zIndex !== undefined ? { zIndex } : undefined}
        onMouseDown={(e) => e.target === e.currentTarget && close()}
      >
        {modalContent}
      </div>,
      document.body,
    );
  }

  return modalContent;
};

export default MasterModal;
